import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  Person,
  LocalHospital,
  Bed,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createICURound, listICUAdmissions } from "../../../../api/icu";
import useFetchObjects from "../../../../api/useFetchObjects";

const NewRound = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Pre-fill from URL params
  const admissionIdParam = searchParams.get("admission");
  const roundTypeParam = searchParams.get("type");

  // State for admission search
  const [admissionSearch, setAdmissionSearch] = useState("");
  const [debouncedAdmissionSearch, setDebouncedAdmissionSearch] = useState("");
  
  // Debounce admission search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdmissionSearch(admissionSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [admissionSearch]);

  // Fetch ICU admissions with search
  const { data: admissions, isLoading: admissionsLoading } = useQuery({
    queryKey: ["icu-admissions-active", debouncedAdmissionSearch],
    queryFn: () => listICUAdmissions({ 
      status: 1,
      search: debouncedAdmissionSearch || undefined
    }, token),
  });

  // Fetch doctors and nurses
  const { data: doctors } = useFetchObjects(["users-doctors"], "users", token);
  const { data: nurses } = useFetchObjects(["users-nurses"], "users", token);

  // Form state
  const [roundForm, setRoundForm] = useState({
    admission: admissionIdParam || "",
    doctor: auth?.user?.user?.id || "",
    round_type: roundTypeParam ? parseInt(roundTypeParam) : 1, // 1=Morning, 2=Evening, 3=Night
    round_date: new Date().toISOString().split("T")[0],
    round_time: new Date().toTimeString().slice(0, 5),
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
    temperature: "",
    blood_pressure_systolic: "",
    blood_pressure_diastolic: "",
    heart_rate: "",
    respiratory_rate: "",
    oxygen_saturation: "",
    nurses_present: [], // Array of nurse IDs
    notes: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [selectedNurses, setSelectedNurses] = useState([]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => createICURound(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["icu-rounds"]);
      queryClient.invalidateQueries(["icu-rounds-dashboard"]);
      navigate("/icu/rounds");
    },
    onError: (error) => {
      console.error("Error creating round:", error);
    },
  });

  // Pre-fill form when admission is selected from URL
  useEffect(() => {
    if (admissionIdParam && admissions) {
      const admission = (
        Array.isArray(admissions) ? admissions : admissions?.results || []
      ).find((a) => a.id === parseInt(admissionIdParam));
      if (admission) {
        setRoundForm((prev) => ({ ...prev, admission: admission.id }));
      }
    }
  }, [admissionIdParam, admissions]);

  const handleSubmit = () => {
    const newErrors = {};

    if (!roundForm.admission) {
      newErrors.admission = t(
        "icu.rounds.errors.admission_required",
        "Admission is required"
      );
    }
    if (!roundForm.doctor) {
      newErrors.doctor = t(
        "icu.rounds.errors.doctor_required",
        "Doctor is required"
      );
    }
    if (!roundForm.subjective) {
      newErrors.subjective = t(
        "icu.rounds.errors.subjective_required",
        "Subjective is required"
      );
    }
    if (!roundForm.objective) {
      newErrors.objective = t(
        "icu.rounds.errors.objective_required",
        "Objective is required"
      );
    }
    if (!roundForm.assessment) {
      newErrors.assessment = t(
        "icu.rounds.errors.assessment_required",
        "Assessment is required"
      );
    }
    if (!roundForm.plan) {
      newErrors.plan = t("icu.rounds.errors.plan_required", "Plan is required");
    }

    setFormErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Combine date and time
      const roundDateTime = new Date(
        `${roundForm.round_date}T${roundForm.round_time}`
      );

      const submitData = {
        admission: roundForm.admission,
        doctor: roundForm.doctor,
        round_type: roundForm.round_type,
        round_date: roundDateTime.toISOString(),
        subjective: roundForm.subjective,
        objective: roundForm.objective,
        assessment: roundForm.assessment,
        plan: roundForm.plan,
        temperature: roundForm.temperature || null,
        blood_pressure_systolic: roundForm.blood_pressure_systolic || null,
        blood_pressure_diastolic: roundForm.blood_pressure_diastolic || null,
        heart_rate: roundForm.heart_rate || null,
        respiratory_rate: roundForm.respiratory_rate || null,
        oxygen_saturation: roundForm.oxygen_saturation || null,
        nurses_present: selectedNurses.map((n) => n.id),
        notes: roundForm.notes || "",
      };

      createMutation.mutate(submitData);
    }
  };

  const filteredAdmissions = Array.isArray(admissions)
    ? admissions
    : admissions?.results || [];

  const filteredDoctors = Array.isArray(doctors)
    ? doctors
    : doctors?.results || [];

  const filteredNurses = (Array.isArray(nurses) ? nurses : nurses?.results || []).filter(
    (user) => user.nurse === true || user.is_admin === true
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/icu/rounds")}
        >
          {t("common.back", "Back")}
        </Button>
        <Typography variant="h4" component="h1">
          {t("icu.rounds.new_round", "New ICU Round")}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Admission Selection */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={filteredAdmissions}
              getOptionLabel={(option) => {
                if (typeof option === 'string' || typeof option === 'number') {
                  const admission = filteredAdmissions.find(a => a.id === option);
                  if (admission) {
                    return `${admission.patient_name || ""} - ${admission.bed_info || ""}${admission.ticket_number_display ? ` (Ticket: ${admission.ticket_number_display})` : ""}`.trim();
                  }
                  return "";
                }
                return `${option.patient_name || ""} - ${option.bed_info || ""}${option.ticket_number_display ? ` (Ticket: ${option.ticket_number_display})` : ""}`.trim();
              }}
              value={filteredAdmissions.find(a => a.id === roundForm.admission) || null}
              onChange={(event, newValue) => {
                setRoundForm({ ...roundForm, admission: newValue?.id || "" });
                setFormErrors({ ...formErrors, admission: "" });
              }}
              onInputChange={(event, newInputValue) => {
                setAdmissionSearch(newInputValue);
              }}
              loading={admissionsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("icu.rounds.admission", "Admission")}
                  placeholder={t("icu.rounds.searchAdmissionPlaceholder", { defaultValue: "Search by patient name, phone, or ticket number..." })}
                  error={!!formErrors.admission}
                  helperText={formErrors.admission}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {admissionsLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {option.patient_name || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.bed_info || "N/A"}
                      {option.ticket_number_display && ` • Ticket: ${option.ticket_number_display}`}
                      {option.patient_phone && ` • Phone: ${option.patient_phone}`}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText={t("icu.rounds.noAdmissionsFound", { defaultValue: "No admissions found" })}
            />
          </Grid>

          {/* Doctor Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!formErrors.doctor}>
              <InputLabel>{t("icu.rounds.doctor", "Doctor")}</InputLabel>
              <Select
                value={roundForm.doctor}
                onChange={(e) => {
                  setRoundForm({ ...roundForm, doctor: e.target.value });
                  setFormErrors({ ...formErrors, doctor: "" });
                }}
              >
                {filteredDoctors.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.first_name} {doctor.last_name}
                    {doctor.username && ` (${doctor.username})`}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.doctor && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.doctor}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Round Type */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>
                {t("icu.rounds.round_type", "Round Type")}
              </InputLabel>
              <Select
                value={roundForm.round_type}
                onChange={(e) =>
                  setRoundForm({ ...roundForm, round_type: e.target.value })
                }
              >
                <MenuItem value={1}>
                  {t("icu.rounds.morning", "Morning Round")}
                </MenuItem>
                <MenuItem value={2}>
                  {t("icu.rounds.evening", "Evening Round")}
                </MenuItem>
                <MenuItem value={3}>
                  {t("icu.rounds.night", "Night Round")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Round Date */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t("icu.rounds.round_date", "Round Date")}
              type="date"
              value={roundForm.round_date}
              onChange={(e) =>
                setRoundForm({ ...roundForm, round_date: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Round Time */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t("icu.rounds.round_time", "Round Time")}
              type="time"
              value={roundForm.round_time}
              onChange={(e) =>
                setRoundForm({ ...roundForm, round_time: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t("icu.rounds.soap", "SOAP Notes")}
            </Typography>
          </Grid>

          {/* Subjective */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("icu.rounds.subjective", "Subjective (S)")}
              multiline
              rows={4}
              value={roundForm.subjective}
              onChange={(e) => {
                setRoundForm({ ...roundForm, subjective: e.target.value });
                setFormErrors({ ...formErrors, subjective: "" });
              }}
              error={!!formErrors.subjective}
              helperText={formErrors.subjective}
              required
            />
          </Grid>

          {/* Objective */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("icu.rounds.objective", "Objective (O)")}
              multiline
              rows={4}
              value={roundForm.objective}
              onChange={(e) => {
                setRoundForm({ ...roundForm, objective: e.target.value });
                setFormErrors({ ...formErrors, objective: "" });
              }}
              error={!!formErrors.objective}
              helperText={formErrors.objective}
              required
            />
          </Grid>

          {/* Assessment */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("icu.rounds.assessment", "Assessment (A)")}
              multiline
              rows={4}
              value={roundForm.assessment}
              onChange={(e) => {
                setRoundForm({ ...roundForm, assessment: e.target.value });
                setFormErrors({ ...formErrors, assessment: "" });
              }}
              error={!!formErrors.assessment}
              helperText={formErrors.assessment}
              required
            />
          </Grid>

          {/* Plan */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("icu.rounds.plan", "Plan (P)")}
              multiline
              rows={4}
              value={roundForm.plan}
              onChange={(e) => {
                setRoundForm({ ...roundForm, plan: e.target.value });
                setFormErrors({ ...formErrors, plan: "" });
              }}
              error={!!formErrors.plan}
              helperText={formErrors.plan}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t("icu.rounds.vital_signs", "Vital Signs")}
            </Typography>
          </Grid>

          {/* Vitals */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t("icu.rounds.temperature", "Temperature")}
              type="number"
              value={roundForm.temperature}
              onChange={(e) =>
                setRoundForm({ ...roundForm, temperature: e.target.value })
              }
              InputProps={{ endAdornment: "°C" }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t("icu.rounds.blood_pressure_systolic", "BP Systolic")}
              type="number"
              value={roundForm.blood_pressure_systolic}
              onChange={(e) =>
                setRoundForm({
                  ...roundForm,
                  blood_pressure_systolic: e.target.value,
                })
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t("icu.rounds.blood_pressure_diastolic", "BP Diastolic")}
              type="number"
              value={roundForm.blood_pressure_diastolic}
              onChange={(e) =>
                setRoundForm({
                  ...roundForm,
                  blood_pressure_diastolic: e.target.value,
                })
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t("icu.rounds.heart_rate", "Heart Rate")}
              type="number"
              value={roundForm.heart_rate}
              onChange={(e) =>
                setRoundForm({ ...roundForm, heart_rate: e.target.value })
              }
              InputProps={{ endAdornment: "bpm" }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("icu.rounds.respiratory_rate", "Respiratory Rate")}
              type="number"
              value={roundForm.respiratory_rate}
              onChange={(e) =>
                setRoundForm({ ...roundForm, respiratory_rate: e.target.value })
              }
              InputProps={{ endAdornment: "/min" }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("icu.rounds.oxygen_saturation", "Oxygen Saturation")}
              type="number"
              value={roundForm.oxygen_saturation}
              onChange={(e) =>
                setRoundForm({
                  ...roundForm,
                  oxygen_saturation: e.target.value,
                })
              }
              InputProps={{ endAdornment: "%" }}
            />
          </Grid>

          {/* Nurses Present */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t("icu.rounds.nurses_present", "Nurses Present")}
            </Typography>
            <Autocomplete
              multiple
              options={filteredNurses}
              getOptionLabel={(option) =>
                `${option.first_name || ""} ${option.last_name || ""}`.trim() ||
                option.username ||
                ""
              }
              value={selectedNurses}
              onChange={(event, newValue) => {
                setSelectedNurses(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("icu.rounds.select_nurses", "Select Nurses")}
                  placeholder={t(
                    "icu.rounds.nurses_placeholder",
                    "Select nurses present during this round"
                  )}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={
                      `${option.first_name || ""} ${
                        option.last_name || ""
                      }`.trim() || option.username
                    }
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("icu.rounds.notes", "Additional Notes")}
              multiline
              rows={3}
              value={roundForm.notes}
              onChange={(e) =>
                setRoundForm({ ...roundForm, notes: e.target.value })
              }
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate("/icu/rounds")}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSubmit}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending
                  ? t("common.saving", "Saving...")
                  : t("common.save", "Save Round")}
              </Button>
            </Box>
          </Grid>

          {/* Error Alert */}
          {createMutation.isError && (
            <Grid item xs={12}>
              <Alert severity="error">
                {t("icu.rounds.error_creating", "Error creating round")}:{" "}
                {createMutation.error?.message ||
                  t("common.unknown_error", "Unknown error")}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default NewRound;
